// Bankkártya create/edit — jobb oldali beúszó FormSheet (a billing-profile-form-sheet mintája). Egy komponens
// KÉT módban: `card == null` → LÉTREHOZÁS (POST), különben SZERKESZTÉS (PATCH If-Match/OCC). A validálás forrás-
// igazsága a @atlas/contracts PaymentCardWriteBodySchema (a resolver a features/payment-cards/form.ts sémáján át
// fut). Create-nél az ügyfél a refs listájából, KERESŐS comboboxszal; edit-nél az ügyfél ZÁROLT (read-only).
// NINCS elsődlegessé-tétel (kártyának nincs elsődlegese).
import { type PaymentCardRowSchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { type ComboboxOption } from '@/components/shared/entity-combobox';
import {
  ComboboxField,
  SelectField,
  TextField,
  TextareaField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { paymentCardKeys } from '@/features/payment-cards/api';
import {
  buildPaymentCardErrorMap,
  CARD_BRANDS,
  EMPTY_PAYMENT_CARD_FORM,
  paymentCardFormSchema,
  paymentCardFormToCreateBody,
  paymentCardFormToWriteBody,
  paymentCardToFormValues,
  type PaymentCardFormValues,
} from '@/features/payment-cards/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type PaymentCardRow = z.infer<typeof PaymentCardRowSchema>;

export interface PaymentCardFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő kártya (hordozza az OCC `version`-t + az előtöltést). */
  card: PaymentCardRow | null;
  /**
   * ELŐTÖLTÖTT, ZÁROLT ügyfél — akkor adjuk meg, ha a sheet egy KONKRÉT ügyfél detail-oldaláról nyílik
   * („hozzáadás" a kapcsolódó szekcióból). Ilyenkor az ügyfél nem választható: a szülő adott, és egy
   * szabadon átállítható választó itt csak hibalehetőség (a rekord véletlenül MÁS ügyfélhez kerülne).
   * A megjelenítés ugyanaz, mint szerkesztéskor: olvasható név, nem beviteli mező.
   */
  lockedClient?: { id: string; name: string } | undefined;
}

export function PaymentCardFormSheet({
  open,
  onOpenChange,
  card,
  lockedClient,
}: PaymentCardFormSheetProps): JSX.Element {
  const { t } = useTranslation();
  const isEdit = card !== null;

  // Ügyfél-választó refs (CSAK create-módban kell — a jog-kapu payment_cards.manage; a BFF adja).
  const refsQ = api.paymentCardClientRefs.useQuery(
    paymentCardKeys.clientRefs(),
    {},
    { queryKey: paymentCardKeys.clientRefs(), enabled: open && !isEdit },
  );
  const clients = refsQ.data?.status === 200 ? refsQ.data.body.clients : [];

  const form = useForm<PaymentCardFormValues>({
    resolver: zodResolver(paymentCardFormSchema, {
      errorMap: buildPaymentCardErrorMap(t),
    }) as Resolver<PaymentCardFormValues>,
    defaultValues: EMPTY_PAYMENT_CARD_FORM,
    mode: 'onTouched',
  });
  const { reset, handleSubmit, setError } = form;

  // A form a megnyitott kártya értékeire áll (a sheet újranyitásakor is friss); create → üres.
  useEffect(() => {
    if (card) reset(paymentCardToFormValues(card));
    // ZÁROLT ügyfél (a szülő detail-oldaláról nyitva): a create-form MÁR a szülővel indul — a `clientId`
    // így a rejtett választó nélkül is kimegy a body-ban, és a kliens-oldali „válassz ügyfelet" őr sem üt be.
    else reset({ ...EMPTY_PAYMENT_CARD_FORM, clientId: lockedClient?.id ?? '' });
  }, [card, lockedClient, reset]);

  const mutation = useEntityMutation<PaymentCardFormValues>({
    mutationFn: (values) =>
      isEdit
        ? api.paymentCardUpdate.mutation({
            params: { id: card.id },
            body: paymentCardFormToWriteBody(values),
            headers: { 'if-match': String(card.version) },
          })
        : api.paymentCardCreate.mutation({ body: paymentCardFormToCreateBody(values) }),
    invalidateKeys: [paymentCardKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY_PAYMENT_CARD_FORM) {
        setError(field as keyof PaymentCardFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit ? t('toast:paymentCards.updated') : t('toast:paymentCards.created'),
      conflict: t('toast:paymentCards.conflict'),
      forbidden: t('toast:paymentCards.forbidden'),
      validation: t('toast:paymentCards.validation'),
      error: t('toast:paymentCards.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    // A clientId csak create-kor kötelező (edit-nél zárolt) — kliens-oldali őr a 422 előtt.
    if (!isEdit && values.clientId.trim() === '') {
      setError('clientId', { message: t('validation:paymentCards.client') });
      return;
    }
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  // A keresős ügyfélválasztó forrása: a betöltött refs (a lista már minden ügyfelet hoz) HELYBEN szűrve.
  const searchClients = useCallback(
    (q: string): Promise<ComboboxOption[]> => {
      const needle = q.trim().toLowerCase();
      const matched = needle
        ? clients.filter((c) => c.name.toLowerCase().includes(needle))
        : clients;
      return Promise.resolve(matched.slice(0, 50).map((c) => ({ id: c.id, label: c.name })));
    },
    [clients],
  );

  const brandOptions = CARD_BRANDS.map((b) => ({ value: b, label: t(`paymentCards.brands.${b}`) }));

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_PAYMENT_CARD_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('paymentCards.editTitle') : t('paymentCards.createTitle')}
      description={isEdit ? (card.holderName ?? undefined) : t('paymentCards.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('paymentCards.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('paymentCards.saving') : t('paymentCards.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        {isEdit || lockedClient ? (
          <div>
            <p className="text-sm font-medium">{t('paymentCards.form.client')}</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {isEdit ? (card.clientName ?? '—') : (lockedClient?.name ?? '—')}
            </p>
          </div>
        ) : (
          <ComboboxField<PaymentCardFormValues>
            name="clientId"
            label={t('paymentCards.form.client')}
            required
            placeholder={t('paymentCards.form.clientPlaceholder')}
            search={searchClients}
          />
        )}

        <TextField<PaymentCardFormValues>
          name="holderName"
          label={t('paymentCards.form.holderName')}
          required
          autoComplete="off"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField<PaymentCardFormValues>
            name="brand"
            label={t('paymentCards.form.brand')}
            required
            options={brandOptions}
          />
          <TextField<PaymentCardFormValues>
            name="last4"
            label={t('paymentCards.form.last4')}
            hint={t('paymentCards.form.last4Hint')}
            required
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<PaymentCardFormValues>
            name="expiryMonth"
            label={t('paymentCards.form.expiryMonth')}
            hint={t('paymentCards.form.expiryMonthHint')}
            required
            autoComplete="off"
          />
          <TextField<PaymentCardFormValues>
            name="expiryYear"
            label={t('paymentCards.form.expiryYear')}
            hint={t('paymentCards.form.expiryYearHint')}
            required
            autoComplete="off"
          />
        </div>

        <TextField<PaymentCardFormValues>
          name="bankName"
          label={t('paymentCards.form.bankName')}
          autoComplete="off"
        />

        <TextareaField<PaymentCardFormValues>
          name="note"
          label={t('paymentCards.form.note')}
          rows={3}
        />
      </Form>
    </FormSheet>
  );
}
